import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Home, MapPin, DollarSign, Square, Bed, Bath, 
  CheckCircle, AlertCircle, LogOut, Phone, Mail, Star, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import RealtorRatings from '../components/RealtorRatings';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const RealtorDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [myLeads, setMyLeads] = useState([]);
  const [activeTab, setActiveTab] = useState('leads');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/realtor/my-leads`, {
        withCredentials: true
      });
      setMyLeads(response.data.leads || []);
    } catch (error) {
      console.error('Fetch error:', error);
      if (error.response?.status === 403) {
        toast.error('You are not an approved realtor');
        navigate('/');
      } else {
        toast.error('Failed to fetch leads');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMarkContacted = async (leadId) => {
    try {
      await axios.post(
        `${API_URL}/api/realtor/leads/${leadId}/mark-contacted`,
        {},
        { withCredentials: true }
      );
      toast.success('Lead marked as contacted!');
      fetchData();
    } catch (error) {
      console.error('Mark contacted error:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as contacted');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  const getMyAssignment = (lead) => {
    return lead.assigned_agents?.find(a => a.realtor_email === user.email);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-orange-600 hover:text-orange-700"
              >
                <Home className="w-5 h-5" />
                Home
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Realtor Dashboard</h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.name || user?.email}</p>
                <p className="text-xs text-gray-500">Approved Realtor</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('leads')}
              className={`flex items-center space-x-2 py-4 px-2 border-b-2 transition-colors ${
                activeTab === 'leads'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Briefcase className="w-5 h-5" />
              <span className="font-medium">My Leads</span>
            </button>
            <button
              onClick={() => setActiveTab('ratings')}
              className={`flex items-center space-x-2 py-4 px-2 border-b-2 transition-colors ${
                activeTab === 'ratings'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Star className="w-5 h-5" />
              <span className="font-medium">Ratings</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'leads' && (
          <>
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
              </div>
            ) : myLeads.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <CheckCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Leads Assigned Yet
            </h3>
            <p className="text-gray-600">
              You'll receive notifications when leads are assigned to you
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {myLeads.map(lead => {
              const myAssignment = getMyAssignment(lead);
              const hasContacted = myAssignment?.contacted_lead_at;
              
              return (
                <div
                  key={lead.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {lead.lead_name}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                        <Phone className="w-4 h-4" />
                        {lead.lead_phone}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                        <Mail className="w-4 h-4" />
                        {lead.lead_email}
                      </div>
                    </div>
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      hasContacted ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {hasContacted ? 'Contacted' : 'New Assignment'}
                    </span>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900">{lead.property_address}</p>
                        <p className="text-sm text-gray-600">
                          {lead.property_city}, TX {lead.property_zip}
                        </p>
                      </div>
                    </div>

                    {lead.estimated_value && (
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          Est. Value: {formatCurrency(lead.estimated_value)}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      {lead.bedrooms && (
                        <div className="flex items-center gap-1">
                          <Bed className="w-4 h-4" />
                          {lead.bedrooms} bed
                        </div>
                      )}
                      {lead.bathrooms && (
                        <div className="flex items-center gap-1">
                          <Bath className="w-4 h-4" />
                          {lead.bathrooms} bath
                        </div>
                      )}
                      {lead.sqft && (
                        <div className="flex items-center gap-1">
                          <Square className="w-4 h-4" />
                          {lead.sqft.toLocaleString()} sqft
                        </div>
                      )}
                    </div>

                    {lead.notes && (
                      <p className="text-sm text-gray-600 italic">
                        "{lead.notes}"
                      </p>
                    )}
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500">
                        {hasContacted ? (
                          <div>
                            <p>Contacted on {new Date(myAssignment.contacted_lead_at).toLocaleDateString()}</p>
                            {myAssignment.response_time_hours && (
                              <p className="mt-1">Response time: {myAssignment.response_time_hours.toFixed(1)} hours</p>
                            )}
                          </div>
                        ) : (
                          <p>Assigned on {new Date(lead.assigned_at).toLocaleDateString()}</p>
                        )}
                        {lead.assigned_agents && lead.assigned_agents.length > 1 && (
                          <p className="mt-1 text-orange-600">Shared with {lead.assigned_agents.length - 1} other agent(s)</p>
                        )}
                      </div>
                      {!hasContacted && (
                        <button
                          onClick={() => handleMarkContacted(lead.id)}
                          className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 text-sm font-medium"
                        >
                          Mark as Contacted
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
          </>
        )}

        {activeTab === 'ratings' && (
          <RealtorRatings user={user} />
        )}
      </div>
    </div>
  );
};

export default RealtorDashboard;
